import {Box, Button, Card, CardContent, Chip, Divider, Typography, Paper} from "@mui/material";
import React from "react";
import {RiskReportTypes} from "../../types";
import {
    ExpandLess,
    ExpandMore,
    Warning
} from '@mui/icons-material';
import palette from "../../theme/palette";
import {
    OffenderPhoto,
    OffenderField,
    formatPhysical,
    formatDemographics,
    offenderTileSx,
    offenderChipSx,
    tileDividerSx,
    chargesBlockSx
} from "./offender-card-parts";

interface Props {
    assessmentData : RiskReportTypes.RiskResponse;
    isDownloadView ?: boolean;
}

const VISIBLE_CAP = 5;
// Tile footprint in the wrap container: 320px wide + m:1 (8px) on each side.
const TILE_FOOTPRINT = 336;

// Name + DOB, not name alone: a different person at the address who happens to
// share the subject's name must NOT be hidden from a safety report. Same name
// AND same DOB is treated as the same registry identity; records without a DOB
// return null and are never subject-filtered by name (empty+empty must not
// match empty+empty). Only old saved reports rely on this key — new data
// carries matchType, which the filters below check first.
const identityKey = (offender: RiskReportTypes.SexOffender): string | null => {
    const dob = String(offender?.sex_offender_birthdate || '').substring(0, 10);
    if (!dob) return null;
    return `${(offender?.sex_offender_first_name || '').toUpperCase()}_${(offender?.sex_offender_last_name || '').toUpperCase()}_${dob}`;
};

// Section 3 of the redesigned report: offenders registered at the searched
// address, excluding the subject (who is shown in section 2). The API already
// dedupes these against the subject match except for generic city searches,
// where merged subject records are tagged matchType 'name_dob'.
export const OffendersAtLocation: React.FC<Props> = ({ assessmentData, isDownloadView }) => {

    // Sparse reports (no offender block) reach this panel — see news-articles.tsx.
    const { nameMatchesInSexOffender, sexOffenderAtLocation } = assessmentData ?? {};
    const [showAll, setShowAll] = React.useState<boolean>(false);

    // Collapsed view shows a single row of tiles: measure how many fit. Falls
    // back to the flat cap when the width can't be measured (jsdom, first paint).
    const tileRowRef = React.useRef<HTMLDivElement>(null);
    const [tilesPerRow, setTilesPerRow] = React.useState<number>(VISIBLE_CAP);
    React.useLayoutEffect(() => {
        const el = tileRowRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const measure = () => {
            const width = el.clientWidth;
            setTilesPerRow(width ? Math.max(1, Math.floor(width / TILE_FOOTPRINT)) : VISIBLE_CAP);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const subjectKeys = new Set(
        (nameMatchesInSexOffender?.matchingRecords ?? [])
            .filter((record) => record?.sex_offender_first_name || record?.sex_offender_last_name)
            .map(identityKey)
            .filter(Boolean)
    );

    let offendersList = (sexOffenderAtLocation ?? []).filter((offender: RiskReportTypes.SexOffender) => {
        if (offender?.matchType === 'name_dob') return false;
        if (offender?.matchDetails?.nameMatch) return false;
        const key = identityKey(offender);
        return !(key && subjectKeys.has(key));
    });

    // Remove duplicates using a Set. ?? '' so a missing charges field never
    // serializes as the string "undefined"; DOB included so two distinct
    // people who share a name (and both lack charges) are not collapsed.
    const seen = new Set();
    offendersList = offendersList.filter((offender: RiskReportTypes.SexOffender) => {
        const key = `${offender.sex_offender_name ?? ''}|${String(offender.sex_offender_birthdate ?? '').substring(0, 10)}|${offender.sex_offender_charges ?? ''}`;
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });

    // One row of tiles (never more than the flat cap) in both the live
    // collapsed view and the download render — the export has no expand
    // affordance, so its overflow shows as the "+N additional" note instead.
    const collapsedCount = Math.min(VISIBLE_CAP, tilesPerRow);
    const visibleOffenders = (showAll && !isDownloadView) ? offendersList : offendersList.slice(0, collapsedCount);
    const overflowCount = offendersList.length - visibleOffenders.length;

    return(
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
                <Box className={'pdf-keep-with-next'} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: palette.darkBlue, p: 1, borderRadius: 1, mr: 2 }}>
                        <Warning sx={{ color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={500}>
                            Sex Offenders at Location
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Registered at this address, other than the patient
                        </Typography>
                    </Box>
                    {offendersList.length > 0 && (
                        <Chip
                            label={`${offendersList.length} AT THIS ADDRESS`}
                            variant="outlined"
                            size="small"
                            sx={{
                                mr: 1,
                                bgcolor: '#eb32231a',
                                color: '#EB3223'
                            }}
                        />
                    )}
                </Box>

                {offendersList.length > 0 ? (
                    <Box sx={{ width: '100%' }}>
                        <Box ref={tileRowRef} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            {visibleOffenders.map((offender: RiskReportTypes.SexOffender, index: number) => (
                                <Paper
                                    key={index}
                                    elevation={0}
                                    className={'pdf-avoid-break'}
                                    sx={{
                                        ...offenderTileSx,
                                        m: 1,
                                        minWidth: 320, // Fixed minimum width
                                        maxWidth: 320, // Fixed maximum width
                                        flexShrink: 0 // Prevent shrinking
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <OffenderPhoto offender={offender} size={56} />
                                        <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                            <Typography
                                                fontWeight={600}
                                                sx={{
                                                    fontSize: 15,
                                                    color: '#212B36',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                className={'text-download-adjust'}
                                            >
                                                {offender.sex_offender_name || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatDemographics(offender)}
                                            </Typography>
                                        </Box>
                                        <Chip label={"AT LOCATION"} size="small" sx={offenderChipSx} />
                                    </Box>

                                    <Divider sx={tileDividerSx} />

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                        <OffenderField
                                            label="ADDRESS"
                                            value={[offender.sex_offender_address_line1, offender.sex_offender_address_line2].filter(Boolean).join(', ')}
                                            sx={{ gridColumn: '1 / -1' }}
                                        />
                                        <OffenderField label="DOB/YOB" value={offender.sex_offender_birthdate} />
                                        <OffenderField label="PHYSICAL" value={formatPhysical(offender)} />
                                        <OffenderField
                                            label="CHARGES"
                                            value={offender.sex_offender_charges}
                                            sx={{ ...chargesBlockSx, gridColumn: '1 / -1' }}
                                        />
                                    </Box>
                                </Paper>
                            ))}
                        </Box>

                        {overflowCount > 0 && isDownloadView && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    + {overflowCount} additional offender{overflowCount > 1 ? 's' : ''} registered at this address
                                </Typography>
                            </Box>
                        )}
                        {!isDownloadView && offendersList.length > collapsedCount && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                <Button
                                    variant="outlined-alternate"
                                    size="small"
                                    startIcon={showAll ? <ExpandLess /> : <ExpandMore />}
                                    onClick={() => setShowAll(!showAll)}
                                >
                                    {showAll ? 'Show less' : `Show all ${offendersList.length} offenders`}
                                </Button>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Paper sx={{ p: 3, border: '1px solid #e0e0e0', textAlign: 'center', width: '100%' }}>
                        <Typography variant="body1" color="info" sx={{ mb: 1 }}>
                            No Other Sex Offenders Registered at This Address
                        </Typography>
                    </Paper>
                )}
            </CardContent>
        </Card>
    )
}
